import { useEffect, useCallback } from 'react';
import { b } from '@ekelhaft-tools/beschau';
import { getRpcClient, createRpcClient } from '../lib/rpc-client';
import {
  useDashboardStore,
  type Deployment,
  type ConnectionStatus,
  type ApprovalRequest as StoreApprovalRequest,
  type PipelineExecution as StorePipelineExecution,
} from '../stores/dashboard';

// ═══════════════════════════════════════════════════════════════
// SCHEMAS (BESCHAU-First)
// ═══════════════════════════════════════════════════════════════

const HealthCheckResultSchema = b.object({
  agentStatus: b.string(),
  services: b.array(
    b.object({
      name: b.string(),
      status: b.string(),
    }),
  ),
});

export type HealthCheckResult = b.infer<typeof HealthCheckResultSchema>;

const DeploymentSchema = b.object({
  id: b.string(),
  artifact: b.string(),
  version: b.string(),
  environment: b.string(),
  status: b.enum(['pending', 'running', 'succeeded', 'failed', 'cancelled', 'rolled_back']),
  startedAt: b.string(),
  completedAt: b.string().nullable(),
  error: b.string().nullable(),
});

// Event schemas for WebSocket subscriptions
const DeployStartedEventSchema = b.object({
  artifact: b.string(),
  version: b.string(),
  environment: b.string(),
  deploymentId: b.string(),
  startedAt: b.string(),
});

const DeployCompletedEventSchema = b.object({
  artifact: b.string(),
  version: b.string(),
  environment: b.string(),
  deploymentId: b.string(),
  success: b.boolean(),
  completedAt: b.string(),
  error: b.string().nullable(),
});

const TriggerDeployResultSchema = b.object({
  success: b.boolean(),
  deploymentId: b.string().nullable(),
  outputs: b.record(b.string(), b.unknown()).nullable(),
  error: b.string().nullable(),
});

export type TriggerDeployResult = b.infer<typeof TriggerDeployResultSchema>;

const RollbackResultSchema = b.object({
  success: b.boolean(),
  currentHash: b.string(),
  previousHash: b.string(),
  reason: b.string(),
  message: b.string(),
  safeguardsApplied: b
    .object({
      maxRollbacksPerHour: b.number(),
      cooldownMs: b.number(),
      rollbacksInLastHour: b.number(),
    })
    .nullable(),
});

export type RollbackResult = b.infer<typeof RollbackResultSchema>;

// Pipeline schemas
const StepResultSchema = b.object({
  stepId: b.string(),
  status: b.enum(['completed', 'failed', 'skipped']),
  output: b.string().nullable(),
  error: b.string().nullable(),
  duration: b.number(),
  retries: b.number(),
});

const PipelineExecutionSchema = b.object({
  executionId: b.string(),
  pipelineName: b.string(),
  status: b.enum(['pending', 'running', 'completed', 'failed', 'cancelled']),
  trigger: b.string(),
  startedAt: b.string(),
  completedAt: b.string().nullable(),
  stepResults: b.array(StepResultSchema),
  totalDuration: b.number().nullable(),
  error: b.string().nullable(),
});

export type PipelineExecution = b.infer<typeof PipelineExecutionSchema>;

const PipelineExecuteResultSchema = b.object({
  success: b.boolean(),
  executionId: b.string().nullable(),
  error: b.string().nullable(),
});

export type PipelineExecuteResult = b.infer<typeof PipelineExecuteResultSchema>;

// Pipeline event schemas
const PipelineStartedEventSchema = b.object({
  executionId: b.string(),
  pipelineName: b.string(),
  trigger: b.string(),
  startedAt: b.string(),
});

const PipelineCompletedEventSchema = b.object({
  executionId: b.string(),
  pipelineName: b.string(),
  status: b.enum(['completed', 'failed', 'cancelled']),
  completedAt: b.string(),
  totalDuration: b.number().nullable(),
  error: b.string().nullable(),
});

/**
 * Handler configuration for deployments.
 */
export interface HandlerConfig {
  handler: string;
  config: Record<string, unknown>;
}

const ApprovalStatusSchema = b.enum(['pending', 'approved', 'rejected', 'expired', 'cancelled']);

const ApprovalRequestSchema = b.object({
  id: b.string(),
  deploymentId: b.string(),
  artifact: b.string(),
  version: b.string(),
  environment: b.string(),
  requestedBy: b.string(),
  createdAt: b.string(),
  expiresAt: b.string().nullable(),
  status: ApprovalStatusSchema,
  reviewedBy: b.string().nullable(),
  reviewedAt: b.string().nullable(),
  reviewComment: b.string().nullable(),
  reason: b.string().nullable(),
  stageName: b.string().nullable(),
  isBreakGlass: b.boolean(),
});

export type ApprovalRequest = b.infer<typeof ApprovalRequestSchema>;

// ═══════════════════════════════════════════════════════════════
// CONFIG MANAGEMENT SCHEMAS
// ═══════════════════════════════════════════════════════════════

const ArtifactTypeSchema = b.enum([
  'ssh',
  'docker-compose',
  'docker',
  'npm',
  'terraform',
  'static',
  'helm',
  'cloudflare-dns',
  'custom',
]);

const ConfigArtifactSchema = b.object({
  id: b.string(),
  name: b.string(),
  type: ArtifactTypeSchema,
  version: b.string(),
  handler: b.string().nullable(),
  dependencies: b.array(b.string()).nullable(),
  config: b.record(b.string(), b.unknown()),
  deployment: b.record(b.string(), b.unknown()).nullable(),
  metadata: b.record(b.string(), b.unknown()).nullable(),
  createdAt: b.string(),
  updatedAt: b.string(),
});

export type ConfigArtifact = b.infer<typeof ConfigArtifactSchema>;

const EnvironmentTierSchema = b.enum(['development', 'staging', 'production']);

const ApprovalConfigSchema = b.object({
  required: b.boolean(),
  approvers: b.array(b.string()).nullable(),
  minApprovals: b.number().nullable(),
});

const ConfigEnvironmentSchema = b.object({
  id: b.string(),
  name: b.string(),
  description: b.string().nullable(),
  tier: EnvironmentTierSchema.nullable(),
  region: b.string().nullable(),
  variables: b.record(b.string(), b.string()).nullable(),
  secrets: b.array(b.string()).nullable(),
  approvals: ApprovalConfigSchema.nullable(),
  constraints: b.record(b.string(), b.unknown()).nullable(),
  notifications: b.record(b.string(), b.unknown()).nullable(),
  metadata: b.record(b.string(), b.unknown()).nullable(),
  createdAt: b.string(),
  updatedAt: b.string(),
});

export type ConfigEnvironment = b.infer<typeof ConfigEnvironmentSchema>;

const AgentModeSchema = b.enum(['root', 'leaf']);
const AgentStatusResponseSchema = b.enum(['healthy', 'degraded', 'unreachable', 'unknown']);

const RegisteredAgentSchema = b.object({
  id: b.string(),
  hostname: b.string(),
  endpoint: b.string(),
  capabilities: b.array(b.string()),
  mode: AgentModeSchema,
  status: AgentStatusResponseSchema,
  version: b.string(),
  registeredAt: b.string(),
  lastHeartbeat: b.string().nullable(),
  metadata: b.record(b.string(), b.unknown()).nullable(),
});

export type RegisteredAgent = b.infer<typeof RegisteredAgentSchema>;

const ExportFileSchema = b.object({
  path: b.string(),
  type: b.enum(['artifact', 'environment']),
});

const ExportResponseSchema = b.object({
  success: b.boolean(),
  files: b.array(ExportFileSchema),
  error: b.string().nullable(),
});

export type ExportFile = b.infer<typeof ExportFileSchema>;
export type ExportResponse = b.infer<typeof ExportResponseSchema>;

export interface ArtifactCreateInput {
  name: string;
  type: b.infer<typeof ArtifactTypeSchema>;
  version: string;
  handler: string | null;
  dependencies: string[] | null;
  config: Record<string, unknown>;
  deployment: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
}

export interface ArtifactUpdateInput {
  version: string | null;
  handler: string | null;
  dependencies: string[] | null;
  config: Record<string, unknown> | null;
  deployment: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
}

export interface EnvironmentCreateInput {
  name: string;
  description: string | null;
  tier: b.infer<typeof EnvironmentTierSchema> | null;
  region: string | null;
  variables: Record<string, string> | null;
  secrets: string[] | null;
  approvals: b.infer<typeof ApprovalConfigSchema> | null;
  constraints: Record<string, unknown> | null;
  notifications: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
}

export interface EnvironmentUpdateInput {
  description: string | null;
  tier: b.infer<typeof EnvironmentTierSchema> | null;
  region: string | null;
  variables: Record<string, string> | null;
  secrets: string[] | null;
  approvals: b.infer<typeof ApprovalConfigSchema> | null;
  constraints: Record<string, unknown> | null;
  notifications: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
}

export interface ApprovalListQuery {
  status: 'pending' | 'approved' | 'rejected' | 'expired' | 'cancelled' | null;
  environment: string | null;
  artifact: string | null;
  limit: number | null;
}

// ═══════════════════════════════════════════════════════════════
// STATE MAPPING
// ═══════════════════════════════════════════════════════════════

/**
 * Map ConnectionState from @geist/rpc-client to simpler ConnectionStatus for UI.
 */
function mapState(state: string): ConnectionStatus {
  if (state === 'ready' || state === 'connected') {
    return 'connected';
  }
  if (state === 'connecting' || state === 'reconnecting' || state === 'syncing') {
    return 'connecting';
  }
  return 'disconnected';
}

// ═══════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════

export function useAgent() {
  const {
    connectionStatus,
    setConnectionStatus,
    addDeployment,
    updateDeployment,
    approvals,
    addApproval,
    updateApproval,
    addNotification,
    addPipeline,
    updatePipeline,
  } = useDashboardStore();

  useEffect(() => {
    // Create client with state change handler
    const client = createRpcClient({
      onStateChange: (state) => {
        setConnectionStatus(mapState(state));
      },
    });

    // Subscribe to deployment events
    const unsubStarted = client.on('deploy.$started', (data) => {
      try {
        const event = DeployStartedEventSchema.parse(data);
        const deployment: Deployment = {
          id: event.deploymentId,
          artifact: event.artifact,
          version: event.version,
          environment: event.environment,
          status: 'running',
          startedAt: event.startedAt,
          completedAt: null,
          error: null,
        };
        addDeployment(deployment);
        addNotification({
          type: 'approval_requested',
          message: `Deploying ${event.artifact}@${event.version} to ${event.environment}`,
        });
      } catch (err) {
        console.error('Failed to parse deploy.$started event:', err);
      }
    });

    const unsubCompleted = client.on('deploy.$completed', (data) => {
      try {
        const event = DeployCompletedEventSchema.parse(data);
        const status = event.success ? 'succeeded' : 'failed';
        updateDeployment(event.deploymentId, {
          status,
          completedAt: event.completedAt,
          error: event.error,
        });

        if (event.success) {
          addNotification({
            type: 'approval_approved',
            message: `Successfully deployed ${event.artifact}@${event.version} to ${event.environment}`,
          });
        } else {
          addNotification({
            type: 'approval_rejected',
            message: `Deployment failed: ${event.artifact}@${event.version} - ${event.error ?? 'Unknown error'}`,
          });
        }
      } catch (err) {
        console.error('Failed to parse deploy.$completed event:', err);
      }
    });

    // Subscribe to approval events
    const unsubApprovalRequested = client.on('approval.$requested', (data) => {
      try {
        const approval = ApprovalRequestSchema.parse(data);
        addApproval(approval as StoreApprovalRequest);
        addNotification({
          type: 'approval_requested',
          message: `Approval requested for ${approval.artifact} to ${approval.environment}`,
        });
      } catch {
        // Ignore invalid event data
      }
    });

    const unsubApprovalApproved = client.on('approval.$approved', (data) => {
      try {
        const approval = ApprovalRequestSchema.parse(data);
        updateApproval(approval.id, approval as StoreApprovalRequest);
        addNotification({
          type: 'approval_approved',
          message: `${approval.artifact} approved by ${approval.reviewedBy ?? 'unknown'}`,
        });
      } catch {
        // Ignore invalid event data
      }
    });

    const unsubApprovalRejected = client.on('approval.$rejected', (data) => {
      try {
        const approval = ApprovalRequestSchema.parse(data);
        updateApproval(approval.id, approval as StoreApprovalRequest);
        addNotification({
          type: 'approval_rejected',
          message: `${approval.artifact} rejected: ${approval.reviewComment ?? 'no reason'}`,
        });
      } catch {
        // Ignore invalid event data
      }
    });

    // Subscribe to pipeline events
    const unsubPipelineStarted = client.on('pipeline.$started', (data) => {
      try {
        const event = PipelineStartedEventSchema.parse(data);
        const pipeline: StorePipelineExecution = {
          executionId: event.executionId,
          pipelineName: event.pipelineName,
          status: 'running',
          trigger: event.trigger,
          startedAt: event.startedAt,
          completedAt: null,
          stepResults: [],
          totalDuration: null,
          error: null,
        };
        addPipeline(pipeline);
        addNotification({
          type: 'approval_requested',
          message: `Pipeline ${event.pipelineName} started`,
        });
      } catch (err) {
        console.error('Failed to parse pipeline.$started event:', err);
      }
    });

    const unsubPipelineCompleted = client.on('pipeline.$completed', (data) => {
      try {
        const event = PipelineCompletedEventSchema.parse(data);
        updatePipeline(event.executionId, {
          status: event.status,
          completedAt: event.completedAt,
          totalDuration: event.totalDuration,
          error: event.error,
        });

        if (event.status === 'completed') {
          addNotification({
            type: 'approval_approved',
            message: `Pipeline ${event.pipelineName} completed successfully`,
          });
        } else {
          addNotification({
            type: 'approval_rejected',
            message: `Pipeline ${event.pipelineName} ${event.status}: ${event.error ?? 'Unknown error'}`,
          });
        }
      } catch (err) {
        console.error('Failed to parse pipeline.$completed event:', err);
      }
    });

    // Start connecting
    setConnectionStatus('connecting');

    return () => {
      unsubStarted();
      unsubCompleted();
      unsubApprovalRequested();
      unsubApprovalApproved();
      unsubApprovalRejected();
      unsubPipelineStarted();
      unsubPipelineCompleted();
    };
  }, [
    setConnectionStatus,
    addDeployment,
    updateDeployment,
    addApproval,
    updateApproval,
    addNotification,
    addPipeline,
    updatePipeline,
  ]);

  // ─────────────────────────────────────────────────────────────────
  // API METHODS (TYPED via Codegen)
  // ─────────────────────────────────────────────────────────────────

  const healthCheck = useCallback(async (): Promise<HealthCheckResult> => {
    const client = getRpcClient();
    // Legacy call until codegen includes this procedure
    const result = await client.call('health.check', { services: null });
    return HealthCheckResultSchema.parse(result);
  }, []);

  const listDeployments = useCallback(async (): Promise<Deployment[]> => {
    const client = getRpcClient();
    const rawResult = await client.call('deploy.list', { limit: null, status: null });
    // Manual parse until codegen coverage expanded
    return b.array(DeploymentSchema).parse((rawResult as any).deployments) as Deployment[];
  }, []);

  const triggerDeploy = useCallback(
    async (
      artifact: string,
      version: string,
      environment: string,
      handlerConfig: HandlerConfig | null,
      dryRun: boolean | null = null,
    ): Promise<TriggerDeployResult> => {
      const client = getRpcClient();
      const rawResult = await client.call('deploy.execute', {
        artifact,
        version,
        environment,
        dryRun,
        handlerConfig,
      });
      return TriggerDeployResultSchema.parse(rawResult);
    },
    [],
  );

  const approveDeployment = useCallback(
    async (requestId: string, comment: string | null): Promise<ApprovalRequest> => {
      const client = getRpcClient();
      const rawResult = await client.call('approval.approve', {
        requestId,
        approvedBy: 'dashboard-user',
        comment,
      });
      return ApprovalRequestSchema.parse(rawResult);
    },
    [],
  );

  const rejectDeployment = useCallback(
    async (requestId: string, reason: string): Promise<ApprovalRequest> => {
      const client = getRpcClient();
      const rawResult = await client.call('approval.reject', {
        requestId,
        rejectedBy: 'dashboard-user',
        reason,
      });
      return ApprovalRequestSchema.parse(rawResult);
    },
    [],
  );

  const listApprovals = useCallback(
    async (query: ApprovalListQuery): Promise<ApprovalRequest[]> => {
      const client = getRpcClient();
      const rawResult = await client.call('approval.list', {
        status: query.status,
        environment: query.environment,
        artifact: query.artifact,
        limit: query.limit,
      });
      // Manual parse
      return b.array(ApprovalRequestSchema).parse((rawResult as any).requests);
    },
    [],
  );

  const rollbackDeployment = useCallback(
    async (deploymentId: string, targetHash: string | null = null): Promise<RollbackResult> => {
      const client = getRpcClient();
      const rawResult = await client.call('deployment.rollback', {
        deploymentId,
        targetHash,
        safeguards: null,
        dryRun: null,
      });
      return RollbackResultSchema.parse(rawResult);
    },
    [],
  );

  const listPipelines = useCallback(
    async (
      pipelineName: string | null = null,
      status: string | null = null,
    ): Promise<StorePipelineExecution[]> => {
      const client = getRpcClient();
      const rawResult = await client.call('pipeline.list', {
        pipelineName,
        status,
        limit: null,
        offset: null,
      });
      return b
        .array(PipelineExecutionSchema)
        .parse((rawResult as any).executions) as StorePipelineExecution[];
    },
    [],
  );

  const executePipeline = useCallback(
    async (pipelineName: string, trigger: string): Promise<PipelineExecuteResult> => {
      const client = getRpcClient();
      const rawResult = await client.call('pipeline.execute', {
        pipelineName,
        trigger,
      });
      return PipelineExecuteResultSchema.parse(rawResult);
    },
    [],
  );

  // ─────────────────────────────────────────────────────────────────
  // SINGULARITY METHODS (NATIVE FLUX)
  // ─────────────────────────────────────────────────────────────────

  /**
   * Generates a 6D Vortex SBOM manifest (Binary Stream)
   */
  const generateSbom = useCallback(async (path: string): Promise<Blob> => {
    const client = getRpcClient();
    return await client.brutalGenerateSbom({ path });
  }, []);

  /**
   * Executes a native FLUX DSL pipeline (Ouroboros)
   */
  const executeFlux = useCallback(async (source: string): Promise<string> => {
    const client = getRpcClient();
    const result = await client.brutalExecuteFlux({ source });
    return result.status;
  }, []);

  /**
   * New 6D Vortex Inspector - Direct WASM Tunnel
   */
  const inspectVortex = useCallback(async (proc: string, handler: (data: any) => void) => {
    const client = getRpcClient();
    // Re-route binary packets to WASM bridge
    return client.tunnel(proc, (buf) => {
      // @ts-ignore - maelstrom-wasm injected globally or imported
      const decoded = globalThis.maelstrom?.process_packet(buf);
      if (decoded) {
        handler(decoded);
      }
    });
  }, []);

  // ─────────────────────────────────────────────────────────────────
  // CONFIG MANAGEMENT METHODS
  // ─────────────────────────────────────────────────────────────────

  const listAgents = useCallback(
    async (
      status: b.infer<typeof AgentStatusResponseSchema> | null = null,
    ): Promise<RegisteredAgent[]> => {
      const client = getRpcClient();
      const rawResult = await client.call('agent.list', { status });
      return b.array(RegisteredAgentSchema).parse((rawResult as any).agents);
    },
    [],
  );

  const listArtifacts = useCallback(
    async (type: b.infer<typeof ArtifactTypeSchema> | null = null): Promise<ConfigArtifact[]> => {
      const client = getRpcClient();
      const rawResult = await client.call('config.artifact.list', { type });
      return b.array(ConfigArtifactSchema).parse((rawResult as any).artifacts);
    },
    [],
  );

  const getArtifact = useCallback(async (name: string): Promise<ConfigArtifact | null> => {
    const client = getRpcClient();
    const rawResult = await client.call('config.artifact.get', { name });
    return ConfigArtifactSchema.nullable().parse((rawResult as any).artifact);
  }, []);

  const createArtifact = useCallback(
    async (
      artifact: ArtifactCreateInput,
    ): Promise<{ success: boolean; artifact: ConfigArtifact | null; error: string | null }> => {
      const client = getRpcClient();
      const rawResult = await client.call('config.artifact.create', { artifact });
      return b
        .object({
          success: b.boolean(),
          artifact: ConfigArtifactSchema.nullable(),
          error: b.string().nullable(),
        })
        .parse(rawResult);
    },
    [],
  );

  const updateArtifact = useCallback(
    async (
      name: string,
      updates: ArtifactUpdateInput,
    ): Promise<{ success: boolean; artifact: ConfigArtifact | null; error: string | null }> => {
      const client = getRpcClient();
      const rawResult = await client.call('config.artifact.update', { name, updates });
      return b
        .object({
          success: b.boolean(),
          artifact: ConfigArtifactSchema.nullable(),
          error: b.string().nullable(),
        })
        .parse(rawResult);
    },
    [],
  );

  const deleteArtifact = useCallback(
    async (name: string): Promise<{ success: boolean; error: string | null }> => {
      const client = getRpcClient();
      const rawResult = await client.call('config.artifact.delete', { name });
      return b.object({ success: b.boolean(), error: b.string().nullable() }).parse(rawResult);
    },
    [],
  );

  const listEnvironments = useCallback(
    async (
      tier: b.infer<typeof EnvironmentTierSchema> | null = null,
    ): Promise<ConfigEnvironment[]> => {
      const client = getRpcClient();
      const rawResult = await client.call('config.environment.list', { tier });
      return b.array(ConfigEnvironmentSchema).parse((rawResult as any).environments);
    },
    [],
  );

  const getEnvironment = useCallback(async (name: string): Promise<ConfigEnvironment | null> => {
    const client = getRpcClient();
    const rawResult = await client.call('config.environment.get', { name });
    return ConfigEnvironmentSchema.nullable().parse((rawResult as any).environment);
  }, []);

  const createEnvironment = useCallback(
    async (
      environment: EnvironmentCreateInput,
    ): Promise<{
      success: boolean;
      environment: ConfigEnvironment | null;
      error: string | null;
    }> => {
      const client = getRpcClient();
      const rawResult = await client.call('config.environment.create', { environment });
      return b
        .object({
          success: b.boolean(),
          environment: ConfigEnvironmentSchema.nullable(),
          error: b.string().nullable(),
        })
        .parse(rawResult);
    },
    [],
  );

  const updateEnvironment = useCallback(
    async (
      name: string,
      updates: EnvironmentUpdateInput,
    ): Promise<{
      success: boolean;
      environment: ConfigEnvironment | null;
      error: string | null;
    }> => {
      const client = getRpcClient();
      const rawResult = await client.call('config.environment.update', { name, updates });
      return b
        .object({
          success: b.boolean(),
          environment: ConfigEnvironmentSchema.nullable(),
          error: b.string().nullable(),
        })
        .parse(rawResult);
    },
    [],
  );

  const deleteEnvironment = useCallback(
    async (name: string): Promise<{ success: boolean; error: string | null }> => {
      const client = getRpcClient();
      const rawResult = await client.call('config.environment.delete', { name });
      return b.object({ success: b.boolean(), error: b.string().nullable() }).parse(rawResult);
    },
    [],
  );

  const exportConfig = useCallback(
    async (
      outputDir: string | null = null,
      types: ('artifacts' | 'environments')[] | null = null,
    ): Promise<ExportResponse> => {
      const client = getRpcClient();
      const rawResult = await client.call('config.export', { outputDir, types });
      return ExportResponseSchema.parse(rawResult);
    },
    [],
  );

  return {
    connectionStatus,
    isConnected: connectionStatus === 'connected',
    healthCheck,
    listDeployments,
    triggerDeploy,
    rollbackDeployment,
    approveDeployment,
    rejectDeployment,
    listApprovals,
    listPipelines,
    executePipeline,
    generateSbom,
    executeFlux,
    inspectVortex,
    // Approval state from store
    approvals,
    // Config management methods
    listAgents,
    listArtifacts,
    getArtifact,
    createArtifact,
    updateArtifact,
    deleteArtifact,
    listEnvironments,
    getEnvironment,
    createEnvironment,
    updateEnvironment,
    deleteEnvironment,
    exportConfig,
  };
}
